import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';

import type {
  DestroyWatchlistMutation,
  DestroyWatchlistMutationVariables,
  UpsertWatchlistMutation,
  UpsertWatchlistMutationVariables,
} from './__generated__/useWatchlists.graphql.ts';

const UPSERT_WATCHLIST_MUTATION = gql`
  mutation UpsertWatchlistMutation($input: upsertWatchlistInput!) {
    upsertWatchlist(input: $input) {
      watchlist {
        slug
        id
        title
        public
      }
      errors {
        code
        message
        path
      }
    }
  }
` as TypedDocumentNode<
  UpsertWatchlistMutation,
  UpsertWatchlistMutationVariables
>;

const DESTROY_WATCHLIST_MUTATION = gql`
  mutation DestroyWatchlistMutation($input: destroyWatchlistInput!) {
    destroyWatchlist(input: $input) {
      errors {
        code
        message
        path
      }
    }
  }
` as TypedDocumentNode<
  DestroyWatchlistMutation,
  DestroyWatchlistMutationVariables
>;

type UpsertWatchlist = {
  title: string;
  watchlistId?: string;
};

export const useWatchlists = () => {
  const { sport } = useSportContext();
  const [upsertWatchlistMutate, { loading: upsertWatchlistLoading }] =
    useMutation(UPSERT_WATCHLIST_MUTATION, {
      showErrorsWithSnackNotification: true,
    });

  const [destroyWatchlistMutate, { loading: destroyWatchlistMutateLoading }] =
    useMutation(DESTROY_WATCHLIST_MUTATION, {
      showErrorsWithSnackNotification: true,
    });

  return {
    upsertWatchlist: {
      mutate: async (
        { title, watchlistId }: UpsertWatchlist,
        makePublic?: boolean
      ) => {
        return upsertWatchlistMutate({
          variables: {
            input: { title, sport, watchlistId, public: makePublic },
          },
        });
      },
      loading: upsertWatchlistLoading,
    },
    destroyWatchlist: {
      mutate: async (watchlistId: string) => {
        return destroyWatchlistMutate({
          variables: { input: { watchlistId } },
        });
      },
      loading: destroyWatchlistMutateLoading,
    },
  };
};
